import React, { useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useMemberCountry, useDeleteMemberCountry, useUpdateMemberCountryStatus } from '@/lib/api/hooks/memberCountry';
import { ActionDropdown, DeleteModal, PublishModal, UnpublishModal, type DropdownAction } from '@/components/ui';
import { showToast } from '@/lib/toast';
import ArticleContent from '@/components/ui/ArticleContent';
import { ArrowLeft, CalendarDays, CheckCircle2, Globe, MapPin, Pencil, Eye } from 'lucide-react';
import type { UpdateMemberCountry } from '@/lib/api/types';

const MemberCountryDetailPage: React.FC = () => {
    const { regionId, memberCountryId } = useParams<{ regionId: string; memberCountryId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const regionName = location.state?.regionName || 'Region';
    const regionTag = location.state?.regionTag;

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);

    const { data: countryData, isLoading, error, refetch: refetchCountry } = useMemberCountry(memberCountryId);
    const country = countryData?.data;

    const { mutate: deleteCountry, isPending: isDeleting } = useDeleteMemberCountry();
    const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateMemberCountryStatus();

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const handleEdit = () => {
        navigate(`/admin/member-countries/${regionId}/edit/${memberCountryId}`, {
            state: { regionName, regionId, regionTag, country }
        });
    };

    const handleDeleteClick = () => {
        setDeleteModalOpen(true);
    };

    const handleStatusUpdateClick = () => {
        if (!country) return;
        setStatusModalOpen(true);
    };

    const confirmDelete = () => {
        if (!memberCountryId) return;

        deleteCountry(
            { memberCountryId, data: {} as UpdateMemberCountry },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success('Member country deleted successfully!');
                        setDeleteModalOpen(false);
                        navigate(`/admin/member-countries/${regionId}`, {
                            state: { regionName, regionTag }
                        });
                    }
                },
                onError: (error: Error) => {
                    showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
                }
            }
        );
    };

    const confirmStatusUpdate = () => {
        if (!country || !memberCountryId) return;
        const newStatus = country.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

        updateStatus(
            { memberCountryId, data: { status: newStatus } },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success(
                            newStatus === 'PUBLISH'
                                ? 'Member country published successfully!'
                                : 'Member country unpublished successfully!'
                        );
                        setStatusModalOpen(false);
                        refetchCountry();
                    }
                },
                onError: (error: Error) => {
                    showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
                }
            }
        );
    };

    const getDropdownActions = (): DropdownAction[] => [
        {
            label: 'Edit',
            onClick: (e) => {
                e.stopPropagation();
                handleEdit();
            }
        },
        {
            label: country?.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
            onClick: (e) => {
                e.stopPropagation();
                handleStatusUpdateClick();
            },
            disabled: isUpdatingStatus
        },
        {
            label: 'Delete',
            onClick: (e) => {
                e.stopPropagation();
                handleDeleteClick();
            },
            variant: 'danger' as const,
            disabled: isDeleting
        }
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#681628] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading country...</p>
                </div>
            </div>
        );
    }

    if (error || !country) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold text-red-600">Error loading country</h2>
                <p className="mt-2 text-gray-600">{error?.message || 'Country not found'}</p>
                <button onClick={() => navigate(`/admin/member-countries/${regionId}`)} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Countries
                </button>
            </div>
        );
    }

    if (!memberCountryId) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold">Member Country ID missing</h2>
                <button onClick={() => navigate(`/admin/member-countries/${regionId}`)} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Countries
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-5">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm mb-4">
                    <Link to="/admin/member-countries" className="text-[#047857] hover:underline">
                        Member Countries
                    </Link>
                    <span className="text-gray-400">▸</span>
                    <Link
                        to={`/admin/member-countries/${regionId}`}
                        state={{ regionName, regionTag }}
                        className="text-[#047857] hover:underline"
                    >
                        {regionName}
                    </Link>
                    <span className="text-gray-400">▸</span>
                    <span className="text-gray-600">{country.countryName || country.title}</span>
                </div>

                {/* Header */}
                <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <button
                            onClick={() => navigate(`/admin/member-countries/${regionId}`, { state: { regionName, regionTag } })}
                            className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </button>

                        <div className="min-w-[260px] flex-1 border-l border-gray-200 pl-4">
                            <div className="flex flex-wrap items-center gap-3">
                                <h1 className="text-4xl font-bold text-gray-900">{country.countryName || country.title}</h1>
                                <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${country.status === 'PUBLISH'
                                    ? 'bg-[#E8F6EE] text-[#0F8A47]'
                                    : 'bg-[#FDF3E6] text-[#B86A00]'
                                    }`}>
                                    {country.status === 'PUBLISH' ? 'Published' : 'Draft'}
                                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                </span>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                                Country Profile • ID: {memberCountryId}
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={handleEdit}
                                className="inline-flex items-center gap-2 rounded-md border border-[#0F8A47] px-4 py-2 text-[#0F8A47] hover:bg-[#E8F6EE]"
                            >
                                <Pencil className="h-4 w-4" />
                                Edit Country
                            </button>
                            <ActionDropdown
                                id={`country-${memberCountryId}`}
                                actions={getDropdownActions()}
                            />
                        </div>
                    </div>
                </div>

                {/* Profile Summary */}
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[220px_1fr]">
                        <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
                            {country.flagUrl && country.flagUrl !== 'N/A' ? (
                                <img
                                    src={country.flagUrl}
                                    alt={`${country.countryName || country.title} flag`}
                                    className="h-36 w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-36 w-full items-center justify-center bg-gray-200 text-3xl font-bold text-gray-400">
                                    {(country.countryName || country.title || '?').charAt(0)}
                                </div>
                            )}
                        </div>

                        <div>
                            <h2 className="text-4xl font-bold text-gray-900">{country.countryName || country.title}</h2>
                            <p className="mt-2 inline-flex items-center gap-2 text-gray-600">
                                <MapPin className="h-4 w-4" />
                                {country.regionName || regionName || 'N/A'}
                            </p>

                            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                                <span className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-gray-700">
                                    <Globe className="h-4 w-4" />
                                    Member Country
                                </span>
                                <span className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-gray-700">
                                    <CheckCircle2 className="h-4 w-4" />
                                    Active Member
                                </span>
                                <span className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-gray-700">
                                    <CalendarDays className="h-4 w-4" />
                                    Since {new Date(country.dateJoined || country.createdAt).getFullYear()}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Key Details */}
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="rounded-lg border border-gray-100 p-4">
                            <p className="text-sm font-medium text-gray-500">Country Name</p>
                            <p className="mt-1 font-semibold text-gray-900">{country.countryName || country.title || 'N/A'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 p-4">
                            <p className="text-sm font-medium text-gray-500">Region</p>
                            <p className="mt-1 font-semibold text-gray-900">{country.regionName || regionName || 'N/A'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 p-4">
                            <p className="text-sm font-medium text-gray-500">Date Joined</p>
                            <p className="mt-1 font-semibold text-gray-900">{formatDate(country.dateJoined)}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 p-4">
                            <p className="text-sm font-medium text-gray-500">Status</p>
                            <span className={`mt-1 inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${country.status === 'PUBLISH'
                                ? 'bg-[#E8F6EE] text-[#0F8A47]'
                                : 'bg-[#FDF3E6] text-[#B86A00]'
                                }`}>
                                {country.status === 'PUBLISH' ? 'Published' : 'Draft'}
                            </span>
                        </div>
                        <div className="rounded-lg border border-gray-100 p-4">
                            <p className="text-sm font-medium text-gray-500">Date Published</p>
                            <p className="mt-1 font-semibold text-gray-900">{formatDate(country.datePublished)}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 p-4">
                            <p className="text-sm font-medium text-gray-500">Last Updated</p>
                            <p className="mt-1 font-semibold text-gray-900">{formatDate(country.updatedAt || null)}</p>
                            <p className="text-xs text-gray-500">by {country.updatedByName && country.updatedByName !== 'N/A' ? country.updatedByName : 'System'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 p-4">
                            <p className="text-sm font-medium text-gray-500">Languages</p>
                            <p className="mt-1 font-semibold text-gray-900">{country.officialLanguage ?? "N/A"}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 p-4">
                            <p className="text-sm font-medium text-gray-500">Visibility</p>
                            <p className="mt-1 inline-flex items-center gap-1 font-semibold text-gray-900">
                                <Eye className="h-4 w-4" />
                                Public
                            </p>
                        </div>
                    </div>
                </div>

                {/* Description */}
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-200 px-5 py-3">
                        <h3 className="text-lg font-semibold text-[#0F8A47]">Description</h3>
                    </div>

                    <div className="p-5">
                        {country.description ? (
                            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                                <ArticleContent content={country.description} className="prose-sm text-gray-700" />
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">No description available.</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Modal */}
            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Member Country"
                subtitle="Are you sure you want to delete this member country?"
                warningText="This action cannot be undone."
                isLoading={isDeleting}
                itemType="Member Country"
            />

            {/* Publish/Unpublish Modals */}
            {country.status === 'DRAFT' ? (
                <PublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    title="Publish Member Country"
                    subtitle="Are you sure you want to publish this member country?"
                    itemType="Member Country"
                    isLoading={isUpdatingStatus}
                />
            ) : (
                <UnpublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    itemType="Member Country"
                    isLoading={isUpdatingStatus}
                />
            )}
        </>
    );
};

export default MemberCountryDetailPage;
